import React from 'react';
import { Link } from 'react-router-dom';

export type BreadcrumbItem = {
    label: string;
    href?: string;
    onClick?: () => void;
    isActive?: boolean;
}

export type BreadcrumbProps = {
    items: BreadcrumbItem[];
    separator?: React.ReactNode;
    className?: string;
    itemClassName?: string;
    activeItemClassName?: string;
    separatorClassName?: string;
}

export const Breadcrumb = ({  items,  separator = '>',  className = '', itemClassName = '', activeItemClassName = '', separatorClassName = '' }: BreadcrumbProps) => {
    
    const handleItemClick = (item: BreadcrumbItem, event: React.MouseEvent) => {
        if (item.onClick) {
            event.preventDefault();
            item.onClick();
        }
    };

    const renderItem = (item: BreadcrumbItem, index: number) => {
        const isLast = index === items.length - 1;
        const isClickable = item.href || item.onClick;
        const isActive = item.isActive || isLast;

        const baseClasses = `tw-inline-flex tw-items-center tw-text-sm ${itemClassName}`;
        const activeClasses = isActive 
            ? `tw-text-gray-900 tw-font-medium ${activeItemClassName}` 
            : 'tw-text-gray-600 hover:tw-text-gray-900';
        const clickableClasses = isClickable && !isActive 
            ? 'tw-cursor-pointer tw-transition-colors tw-duration-200' 
            : '';

        const itemClasses = `${baseClasses} ${activeClasses} ${clickableClasses}`;

        if (item.href && !isActive) {
            return (
                <Link
                    key={index}
                    to={item.href}
                    className={itemClasses}
                    onClick={(e) => handleItemClick(item, e)}
                >
                    {item.label}
                </Link>
            );
        }

        if (item.onClick && !isActive) {
            return (
                <button
                    key={index}
                    type="button"
                    className={itemClasses}
                    onClick={(e) => handleItemClick(item, e)}
                >
                    {item.label}
                </button>
            );
        }

        return (
            <span key={index} className={itemClasses}>
                {item.label}
            </span>
        );
    };

    const renderSeparator = (index: number) => {
        const separatorClasses = `tw-mx-2 tw-text-gray-400 tw-select-none ${separatorClassName}`;
        
        return (
            <span key={`separator-${index}`} className={separatorClasses}>
                {separator}
            </span>
        );
    };

    if (!items || items.length === 0) {
        return null;
    }

    return (
        <nav className={`tw-flex tw-items-center tw-text-sm ${className}`} aria-label="Breadcrumb">
            <ol className="tw-flex tw-items-center tw-space-x-0">
                {items.map((item, index) => (
                    <React.Fragment key={index}>
                        <li className="tw-flex tw-items-center">
                            {renderItem(item, index)}
                        </li>
                        {index < items.length - 1 && (
                            <li className="tw-flex tw-items-center">
                                {renderSeparator(index)}
                            </li>
                        )}
                    </React.Fragment>
                ))}
            </ol>
        </nav>
    );
};